import { Component, DestroyRef, inject, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';

import {
  RdsButtonModule,
  RdsIconsModule,
  RdsFormFieldModule,
  RdsProgressSpinnerComponent,
  RdsDrawerActionsDirective,
} from '@rds/angular-components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DetailedViewObjectService } from '../../services/detailed-view-object.service';
import { StrategicPillarService, StrategicPillar, StrategicPillarRaw } from '../../services/strategic-pillar.service';

export type StrategicPillarFormValidity = "valid" | "warning" | "invalid_priority" | "required" | "delete" | "error";

type StrategicPillarForm = FormGroup<{
  title: FormControl<string | null>;
  description: FormControl<string | null>;
  color_code: FormControl<string | null>;
}>;

@Component({
  selector: 'app-strategic-pillar-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RdsButtonModule,
    RdsIconsModule,
    RdsFormFieldModule,
    RdsProgressSpinnerComponent,
    RdsDrawerActionsDirective
],
  templateUrl: './strategic-pillar-form.component.html',
  styleUrls: ['./strategic-pillar-form.component.scss']
})
export class StrategicPillarFormComponent implements OnInit {
  @Input() selectedStrategicPillar: StrategicPillarRaw | null = null;
  @Input() isLoadingChanges = false;
  @Output() submitStrategicPillar = new EventEmitter<{data: StrategicPillarRaw, valid: StrategicPillarFormValidity}>();
  @Output() deleteStrategicPillar = new EventEmitter<StrategicPillarRaw>();
  @Output() cancelForm = new EventEmitter<void>();

  private readonly detailedViewObjectService = inject(DetailedViewObjectService);
  private readonly strategicPillarService = inject(StrategicPillarService);
  private readonly destroyRef = inject(DestroyRef);

  strategicPillarForm!: StrategicPillarForm;

  // Predefined pastel color palette
  readonly colorOptions = [
    '#FF6B9D', // Bright Pink
    '#4ECDC4', // Teal
    '#FFE66D', // Bright Yellow
    '#95E1D3', // Mint Green
    '#A8E6CF', // Light Green
    '#C7CEEA', // Lavender Blue
    '#FECA57', // Orange
    '#FF9FF3', // Magenta
    '#54A0FF', // Blue
    '#DDA0DD'  // Plum
  ];

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.setupForm();
    this.subscribeToDetailedObject();
  }

  private subscribeToDetailedObject() {
    this.detailedViewObjectService.detailedObject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((obj: any) => {
        if (obj instanceof StrategicPillarRaw) {
          this.selectedStrategicPillar = obj;
          this.setupForm();
        }
      });
  }

  private setupForm() {
    const pillar = this.selectedStrategicPillar || new StrategicPillarRaw();
    
    this.strategicPillarForm = this.fb.group({
      title: [pillar.title || '', Validators.required],
      description: [pillar.description || '', Validators.required],
      color_code: [pillar.color_code || '#FFB3BA', Validators.required]
    }) as StrategicPillarForm;
  }

  selectColor(color: string) {
    this.strategicPillarForm.patchValue({ color_code: color });
  }

  onSubmit() {
    if (this.strategicPillarForm.valid && this.selectedStrategicPillar) {
      const formData = {
        ...this.selectedStrategicPillar,
        ...this.strategicPillarForm.value
      } as StrategicPillarRaw;

      this.submitStrategicPillar.emit({
        data: formData,
        valid: 'valid'
      });
    } else {
      this.strategicPillarForm.markAllAsTouched();
      this.submitStrategicPillar.emit({
        data: this.selectedStrategicPillar || new StrategicPillarRaw(),
        valid: 'required'
      });
    }
  }

  onDelete() {
    if (this.selectedStrategicPillar && this.selectedStrategicPillar.id !== 0) {
      this.deleteStrategicPillar.emit(this.selectedStrategicPillar);
    }
  }

  closeForm() {
    this.cancelForm.emit();
  }
}